import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { MenuItem, MenuItemComponent } from './menu-item.component'

export const menuComponentTestIds = {
  container: 'menu-container',
}

export interface MenuItemGroup {
  key: string
  items: MenuItem[]
}

export type MenuItems = MenuItem[] | MenuItemGroup[]

@Component({
  selector: 'ftr-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css'],
  imports: [MenuItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent {
  readonly menuItems = input.required<MenuItems>()
  readonly trackClicks = input<boolean>()
  readonly maxWidth = input<number>(20)
  readonly minWidth = input<number>()
  readonly itemsBeforeScroll = input<number>()

  readonly itemClicked = output()

  readonly menuItemGroups = computed<MenuItemGroup[]>(() => {
    const items = this.menuItems()
    return isMenuItemGroup(items) ? items : [{ key: 'items', items }]
  })

  /**
   * The menu's max-height in rem, or undefined when there are no more items than `itemsBeforeScroll`.
   * The template sets overflow from this value as well. A menu that fits must not become a scroll
   * container, because a scroll container clips the focus outlines on its items.
   *
   * The height leaves half of the next item in view. This shows the user that the menu scrolls. An
   * item is 2.5rem tall only while its label fits one line. Only the top padding counts, because the
   * bottom padding scrolls out of view.
   */
  protected readonly maxHeight = computed(() => {
    const itemsBeforeScroll = this.itemsBeforeScroll()
    const totalItems = this.menuItemGroups().reduce((total, group) => total + group.items.length, 0)

    if (!itemsBeforeScroll || totalItems <= itemsBeforeScroll) {
      return undefined
    }

    const itemHeightRem = 2.5
    const topPaddingRem = 0.5
    const visibleItems = itemsBeforeScroll + 0.5

    return `${topPaddingRem + visibleItems * itemHeightRem}rem`
  })

  protected readonly testIds = menuComponentTestIds
}

function isMenuItemGroup(items: MenuItems): items is MenuItemGroup[] {
  return items.length > 0 && 'key' in items[0]
}
